import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Activity, ExternalLink } from 'lucide-react';
import { springConfig } from '../utils/animations';
import { FUNCTION_HEALTH_AFFILIATE_LINK } from '../utils/constants';

interface FunctionHealthMiniCTAProps {
  className?: string;
  message?: string;
  buttonText?: string;
}

const FunctionHealthMiniCTA: React.FC<FunctionHealthMiniCTAProps> = ({
  className = '',
  message = 'Track your health with real data',
  buttonText = 'Learn More'
}) => {
  const prefersReducedMotion = useReducedMotion();

  const handleClick = () => {
    window.open(FUNCTION_HEALTH_AFFILIATE_LINK, '_blank', 'noopener,noreferrer');
  };

  // Button hover animation variants
  const buttonVariants = {
    rest: { scale: 1 },
    hover: {
      scale: 1.02,
      transition: springConfig.snappy
    },
    tap: {
      scale: 0.98,
      transition: { duration: 0.1 }
    }
  };

  // Reduced motion version
  if (prefersReducedMotion) {
    return (
      <div className={`${className}`}>
        <div className="bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-catppuccin-surface1 dark:to-catppuccin-surface1 rounded-lg p-4 border border-teal-100 dark:border-catppuccin-overlay0">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-teal-100 dark:bg-catppuccin-teal/20 flex items-center justify-center flex-shrink-0">
                <Activity size={16} className="text-teal-600 dark:text-catppuccin-teal" />
              </div>
              <p className="text-sm text-gray-600 dark:text-catppuccin-subtext0">
                {message}
              </p>
            </div>
            <button
              onClick={handleClick}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-600 dark:bg-catppuccin-teal text-white dark:text-catppuccin-base text-sm font-medium rounded-full hover:bg-teal-700 dark:hover:bg-catppuccin-green transition-colors duration-200 flex-shrink-0"
            >
              <span>{buttonText}</span>
              <ExternalLink size={14} />
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-400 dark:text-catppuccin-overlay1 text-center sm:text-left">
            Partner link - we may earn a commission
          </p>
        </div>
      </div>
    );
  }

  // Animated version
  return (
    <motion.div
      className={`${className}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springConfig.gentle}
    >
      <motion.div
        className="bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-catppuccin-surface1 dark:to-catppuccin-surface1 rounded-lg p-4 border border-teal-100 dark:border-catppuccin-overlay0"
        whileHover={{ y: -1, boxShadow: '0 4px 12px -2px rgba(0, 0, 0, 0.08)' }}
        transition={springConfig.gentle}
      >
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <motion.div
              className="w-8 h-8 rounded-full bg-teal-100 dark:bg-catppuccin-teal/20 flex items-center justify-center flex-shrink-0"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Activity size={16} className="text-teal-600 dark:text-catppuccin-teal" />
            </motion.div>
            <p className="text-sm text-gray-600 dark:text-catppuccin-subtext0">
              {message}
            </p>
          </div>
          <motion.button
            onClick={handleClick}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-600 dark:bg-catppuccin-teal text-white dark:text-catppuccin-base text-sm font-medium rounded-full hover:bg-teal-700 dark:hover:bg-catppuccin-green transition-colors duration-200 flex-shrink-0"
            variants={buttonVariants}
            initial="rest"
            whileHover="hover"
            whileTap="tap"
          >
            <span>{buttonText}</span>
            <ExternalLink size={14} />
          </motion.button>
        </div>
        <motion.p
          className="mt-2 text-xs text-gray-400 dark:text-catppuccin-overlay1 text-center sm:text-left"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          Partner link - we may earn a commission
        </motion.p>
      </motion.div>
    </motion.div>
  );
};

export default FunctionHealthMiniCTA;
